import { getExtensionFromMimeType } from '../../../../utils/mediaLibrary'
import Papa from 'papaparse'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
} from '../../../interfaces'

interface CSVRow {
  'Asset ID': string
  Type: string
  Name: string
  Path: string
  'Created At': string
  URL: string
  Format: string
  Size: string
}

// Converts a file to CSV row format
const fileToCSVRow = (file: MediaLibrarySearchFileResponse): CSVRow => ({
  'Asset ID': file.type === 'file-version' ? file.versionInfo.id : file.fileId,
  Type: file.type === 'file-version' ? 'File version' : 'File',
  Name: file.name,
  Path: file.filePath,
  'Created At': new Date(file.createdAt).toISOString(),
  URL: file.url || '',
  Format: getExtensionFromMimeType(file.mime) ?? '',
  Size: file.size ? String(file.size) : '',
})

// Converts a folder to CSV row format
const folderToCSVRow = (folder: MediaLibrarySearchFolderResponse): CSVRow => ({
  'Asset ID': folder.folderId,
  Type: 'Folder',
  Name: folder.name,
  Path: folder.folderPath,
  'Created At': new Date(folder.createdAt).toISOString(),
  URL: '',
  Format: '',
  Size: '',
})

// Converts a media collection to CSV row format
const mediaCollectionToCSVRow = (
  collection: MediaLibrarySearchCollectionResponse,
): CSVRow => ({
  'Asset ID': collection.mediaCollectionId,
  Type: 'Media collection',
  Name: collection.name,
  Path: '',
  'Created At': new Date(collection.createdAt).toISOString(),
  URL: '',
  Format: '',
  Size: '',
})

// Generates CSV content from selected items
export const generateCSV = (
  files: MediaLibrarySearchFileResponse[],
  folders: MediaLibrarySearchFolderResponse[],
  mediaCollections: MediaLibrarySearchCollectionResponse[],
): string => {
  const headers: Array<keyof CSVRow> = [
    'Asset ID',
    'Type',
    'Name',
    'Path',
    'Created At',
    'URL',
    'Format',
    'Size',
  ]

  // Convert all items to CSV rows (folders first, then files, then media collections)
  const rows: CSVRow[] = [
    ...folders.map(folderToCSVRow),
    ...files.map(fileToCSVRow),
    ...mediaCollections.map(mediaCollectionToCSVRow),
  ]

  // Use papaparse to generate CSV string
  return Papa.unparse({
    fields: headers,
    data: rows,
  }, {
    quotes: true,
    quoteChar: '"',
    delimiter: ',',
    newline: '\n',
    header: true,
    skipEmptyLines: true,
  })
}

// Downloads CSV file with given content
export const downloadCSV = (
  csvContent: string,
  filename = 'media-library-items.csv',
): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')

  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Main function to export selected items as CSV
export const exportSelectedItemsAsCSV = (
  files: MediaLibrarySearchFileResponse[],
  folders: MediaLibrarySearchFolderResponse[],
  mediaCollections: MediaLibrarySearchCollectionResponse[],
): void => {
  const csvContent = generateCSV(files, folders, mediaCollections)
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  const timestamp = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`
  const filename = `media-library-export-${timestamp}.csv`

  downloadCSV(csvContent, filename)
}
